import ReactECharts from 'echarts-for-react';
import type { WidgetConfig } from '../schemas';
import { useFieldCatalog, metricDef, metricLabel, metricFormat, type FieldCatalog } from '../fieldCatalog';
import { formatMetricValue } from '../formatValue';
import { aggregateMetric } from '../aggregate';
import { CHART_COLORS, COLOR_PRIMARY, TITLE_STYLE } from '../chartStyle';
import { resolveTarget } from './target';

type Row = Record<string, string | number>;

interface WidgetData {
  current: Row[];
  previous: Row[] | null;
  meta: any;
}

interface WidgetProps {
  config: WidgetConfig;
  data: WidgetData | null;
  loading?: boolean;
  error?: string;
}

// Cadre fourni par `WidgetShell` (WidgetRenderer) : ici, padding + hauteur seulement.
const SHELL = 'h-full';

export interface GaugeThreshold {
  /** Avancement vers la cible (%) à partir duquel la couleur s'applique. */
  from: number;
  color: string;
}

/**
 * Seuils de couleur par défaut, exprimés en % d'avancement vers la cible.
 * Couleurs issues de la palette partagée (`chartStyle`), jamais codées en dur ici :
 * loin de la cible → ambre, en route → indigo, cible quasi atteinte → vert.
 */
export const GAUGE_THRESHOLDS: GaugeThreshold[] = [
  { from: 0, color: CHART_COLORS[2] },
  { from: 50, color: COLOR_PRIMARY },
  { from: 90, color: CHART_COLORS[1] },
];

/** Cible de la jauge : `display.target`, ou `display.max` (alias historique). `null` si absente ou invalide. */
export function gaugeTarget(config: WidgetConfig): number | null {
  const display = (config.display ?? {}) as Record<string, unknown>;
  return resolveTarget(display.target ?? display.max);
}

/** Dernier seuil franchi par l'avancement. */
function resolveThresholdColor(percent: number, thresholds: GaugeThreshold[]): string {
  const sorted = [...thresholds].sort((a, b) => a.from - b.from);
  let color = sorted[0]?.color ?? COLOR_PRIMARY;
  for (const t of sorted) {
    if (percent >= t.from) color = t.color;
  }
  return color;
}

/**
 * Options ECharts de la jauge — fonction pure, testée sans DOM.
 *
 * La jauge réduit une métrique à un scalaire : l'agrégation passe par `aggregateMetric`
 * (ratio-safe — un taux de rebond se recompose, il ne se moyenne pas).
 * L'aiguille est bornée à la cible, mais le détail affiche la valeur réelle formatée.
 *
 * Sans cible valide, aucune série : même contrat que `ProgressBarWidget` (« À configurer »).
 *
 * ECharts 6 : l'API `gauge` (progress / axisLine / detail / pointer) est inchangée par
 * rapport à la v5 — seul `axisLine.lineStyle.color` reste un tableau de paliers `[[1, c]]`.
 */
export function buildGaugeOption(config: WidgetConfig, data: WidgetData, catalog: FieldCatalog | null) {
  const metric = config.metrics?.[0];
  const display = (config.display ?? {}) as Record<string, unknown>;

  const hasTitle = !!config.title;
  const title = hasTitle
    ? { text: config.title, left: 'center' as const, top: 4, ...TITLE_STYLE }
    : undefined;

  const target = gaugeTarget(config);

  if (!metric || !data.current.length || target === null) {
    return { title, series: [] as object[] };
  }

  const def = metricDef(catalog, metric);
  const format = metricFormat(catalog, metric);
  const label = metricLabel(catalog, metric, config.metric_labels);

  const value = aggregateMetric(data.current, metric, def);

  const min = (display.min as number | undefined) ?? 0;
  const max = target > min ? target : min + 1;

  // Avancement vers la cible : c'est lui qui pilote la couleur et la position de l'aiguille.
  const percent = Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));
  const clamped = Math.max(min, Math.min(max, value));

  const thresholds = (display.thresholds as GaugeThreshold[] | undefined) ?? GAUGE_THRESHOLDS;
  const color = (config.format?.color as string | undefined) ?? resolveThresholdColor(percent, thresholds);

  const showPointer = (display.show_pointer as boolean | undefined) ?? true;
  const showAxisLabel = (display.show_axis_label as boolean | undefined) ?? false;

  return {
    title,
    series: [
      {
        type: 'gauge',
        min,
        max,
        center: ['50%', hasTitle ? '58%' : '55%'],
        radius: '85%',
        startAngle: 210,
        endAngle: -30,
        progress: { show: true, width: 12, itemStyle: { color } },
        axisLine: { lineStyle: { width: 12, color: [[1, '#F4F5FA']] } },
        axisTick: { show: false },
        splitLine: { show: showAxisLabel },
        // Le détail affiche la valeur réelle formatée (pas la valeur bornée par la cible).
        axisLabel: {
          show: showAxisLabel,
          fontSize: 9,
          color: '#8B8FA7',
          distance: 15,
          formatter: (v: number) => formatMetricValue(v, format),
        },
        pointer: { show: showPointer, length: '60%', width: 5, itemStyle: { color } },
        itemStyle: { color },
        detail: {
          fontSize: 16,
          offsetCenter: [0, '72%'],
          color: '#1B2559',
          fontWeight: 600,
          formatter: () => formatMetricValue(value, format),
        },
        data: [{ value: clamped, name: label }],
        title: { show: false },
      },
    ] as object[],
  };
}

export default function GaugeWidget({ config, data, loading, error }: WidgetProps) {
  // Hook avant tout early return (règles des hooks).
  const catalog = useFieldCatalog(data?.meta?.source_type as string | undefined);

  if (loading) {
    return (
      <div className={SHELL}>
        <div className="h-full w-full animate-pulse rounded bg-[#F4F5FA]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${SHELL} flex items-center justify-center`}>
        <span className="text-sm text-red-600">{error}</span>
      </div>
    );
  }

  const metric = config.metrics?.[0];
  const target = gaugeTarget(config);

  // Une jauge sans cible n'a rien à mesurer : même contrat que ProgressBarWidget.
  if (!data || !config.data_source_id || !metric || target === null) {
    return (
      <div className={`${SHELL} flex items-center justify-center`}>
        <span className="text-sm text-[#8B8FA7]">À configurer</span>
      </div>
    );
  }

  const option = buildGaugeOption(config, data, catalog);
  const display = (config.display ?? {}) as Record<string, unknown>;
  const showTarget = (display.show_target as boolean | undefined) ?? true;
  const format = metricFormat(catalog, metric);

  return (
    <div className={`${SHELL} flex flex-col`}>
      <ReactECharts option={option} notMerge style={{ flex: 1, minHeight: 140 }} />
      {showTarget && (
        <div className="text-center text-xs text-[#8B8FA7]">
          Objectif : {formatMetricValue(target, format)}
        </div>
      )}
    </div>
  );
}
